/**
 * @file
 *
 * ユーザーの操作ログを記録するためのユーティリティ。
 */

/** google tagManegertに送信する値。 */
type Event = {
  /** newsの詳細を開く際のEvent */
  clickNewsId: {
    parameters: {
      /** newsのid */
      news_id: string
      /** newsのタイトル */
      news_title: string
      /** 現在いるパス */
      current_path: string
    }
  }
  /** headerのmenuをクリックした際のEvent */
  clickHeaderMenu: {
    parameters: {
      /** 現在いるパス */
      current_path: string
      /** クリックしたmenuのパス */
      next_path: string
    }
  }
  /** footerのmenuをクリックした際のEvent */
  clickFooterMenu: {
    parameters: {
      /** 現在いるパス */
      current_path: string
      /** クリックしたmenuのパス */
      next_path: string
    }
  }
  /** 画面描画時に発火するイベント */
  transitionPage: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /** CTAエリアの「contact」をクリックした際のEvent */
  clickCtaContactButton: {
    parameters: {
      /** 現在いるパス */
      current_path: string
      /** クリックしたmenuのパス */
      next_path: string
    }
  }
  /** CTAエリアの「entry」をクリックした際のEvent */
  clickCtaEntryButton: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /** ヘッダーの「entry」をクリックした際のEvent */
  clickHeaderEntryButton: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /** フッターの「entry」をクリックした際のEvent */
  clickFooterEntryButton: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /** 給与計算のボタンをクリックした際のEvent */
  clickSalaryCalculationButton: {
    parameters: {
      /** 計算タイプ */
      calculation_type: 'monthlyIncome' | 'annualIncome'
      /** リーダー案件 */
      leader_project: 'inPlanning' | 'notParticipating'
      /** 顧客請求単価 */
      client_billing_rate: number
    }
  }
  /** 案件の詳細を開く際のEvent */
  clickProjectsId: {
    parameters: {
      /** 案件のid */
      projects_id: string
      /** 案件のタイトル */
      projects_title: string
      /** 現在いるパス */
      current_path: string
    }
  }
  /** スライダーの「次へ」を押下 */
  clickNextProject: {}
  /** スライダーの「前へ」を押下 */
  clickBackProject: {}
  /** 「案件一覧」（service）を押下 */
  clickBackService: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /**「お知らせ一覧に戻る」を押下 */
  clickBackNewsPage: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /** 「お知らせ一覧を見る」を押下 */
  clickAllNewsPage: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
  /** ページネーションの「次のページ」を押下 */
  clickNextPagination: {
    parameters: {
      /** 現在いるページ番号 */
      current_index: string
      /** 次に遷移するページ番号 */
      next_index: string
    }
  }
  /** ページネーションの「前のページ」を押下 */
  clickBackPagination: {
    parameters: {
      /** 現在いるページ番号 */
      current_index: string
      /** 次に遷移するページ番号 */
      next_index: string
    }
  }
  /** ページネーションの数値を押下 */
  clickIndexPagination: {
    parameters: {
      /** 現在いるページ番号 */
      current_index: string
      /** 次に遷移するページ番号 */
      next_index: string
    }
  }
  /** お問い合わせの種類を選択 */
  clickContactType: {
    parameters: {
      /** 現在いるパス */
      contact_type: string
    }
  }
  /** 「送信する」を押下 */
  submitContactType: {
    parameters: {
      /** 現在いるパス */
      contact_type: string
    }
  }
  /**「topに戻る」を押下 */
  clickBackTop: {
    parameters: {
      /** 現在いるパス */
      current_path: string
    }
  }
}

/**
 * Activity Event を発火する。
 */
export const fireEvent = async <EventName extends keyof Event>(
  args: Event[EventName] extends { parameters: infer Parameters }
    ? { event: EventName; parameters: Parameters }
    : { event: EventName; parameters?: never },
): Promise<void> => {
  const { event, parameters } = args
  if (typeof window !== 'undefined') {
    window.dataLayer ??= []
    window.dataLayer.push({
      event,
      request_id: crypto.randomUUID(),
      ...(parameters ?? {}),
    })
    // devtoolのconsoleにlogが出力されすぎるのでコメントアウト
    // console.log(
    //   '\x1b[32m%s \x1b[0m',
    //   `Done FireEvent Name: ${event}, Parameters: ${JSON.stringify(
    //     parameters,
    //   )}`,
    // )
  } else {
    console.warn('window is not defined. Event not fired:', event)
  }
}

// 本来プロジェクト内の適当な箇所にこの型が定義されていれば問題ないはずだが、
// なぜかエラーになってしまう。利用箇所と同じファイルに型定義することでこれを回避する。
declare global {
  interface Window {
    /**
     * GTM が提供するイベント送信処理向けの拡張。
     * 正しく GTM が有効化されている場合、この値に push することで対象の値が記録される。
     */
    dataLayer: Array<Record<string, any>>
  }
}
